import React from "react"

import { format, parseISO } from "date-fns"
import { Calendar, Clock, Info, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
  INVALID_UK_TIME_ERROR,
  SHIFT_START_IN_PAST_ERROR,
  isShiftStartInPast,
  ukToday,
  ukWallClockToInstant,
} from "@/constants/sheriffOptions"
import { useClearUnavailableLocation } from "@/hooks/useClearUnavailableLocation"
import { useDepots } from "@/hooks/useDepots"
import { cn } from "@/lib/utils"
import { Person } from "@/types/person"
import { selectableDepots } from "@/utils/depotValidity"
import { formatPersonShortName } from "@/utils/personDisplay"

export interface NewShift {
  startDate: string // ISO date yyyy-mm-dd
  endDate: string // ISO date yyyy-mm-dd
  startTime: string // HH:mm
  endTime: string // HH:mm
  area: string
  location: string
  depotId?: string | null
  notes?: string
}

interface AddShiftDialogProps {
  person: Person
  open: boolean
  onOpenChange: (open: boolean) => void
  onShiftCreated?: (shift: NewShift) => void
}

const AddShiftDialog: React.FC<AddShiftDialogProps> = ({
  person,
  open,
  onOpenChange,
  onShiftCreated,
}) => {
  const [isSaving, setIsSaving] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)
  const { data: depots, isLoading: depotsLoading } = useDepots()

  const personAreas = person.sheriff_areas ?? []
  const personType = person.sheriff_type
  const personRole = person.employer
  const displayedType = personType || "No sheriff type on person"
  const displayedRole = personRole || "No role on person"

  const [formData, setFormData] = React.useState<NewShift>({
    startDate: ukToday(),
    endDate: ukToday(),
    startTime: "09:00",
    endTime: "17:00",
    area: personAreas[0] || "",
    location: "",
  })

  // The person's locations that are open for the block's dates
  const availableDepots = React.useMemo(() => {
    const allowedLocations = new Set(
      (person.location || []).map((location) => location.toLowerCase()),
    )

    return selectableDepots(depots || [], formData.startDate, formData.endDate).filter((depot) =>
      allowedLocations.has(depot.name.toLowerCase()),
    )
  }, [depots, person.location, formData.startDate, formData.endDate])

  // Drop a chosen location once a date change makes it unavailable
  useClearUnavailableLocation(formData.location, availableDepots, Boolean(depots), () =>
    setFormData((prev) => ({ ...prev, location: "" })),
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const startDateTime = ukWallClockToInstant(formData.startDate, formData.startTime)
    const endDateTime = ukWallClockToInstant(formData.endDate, formData.endTime)
    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
      setFormError(INVALID_UK_TIME_ERROR)
      return
    }
    if (isShiftStartInPast(startDateTime)) {
      setFormError(SHIFT_START_IN_PAST_ERROR)
      return
    }
    if (endDateTime <= startDateTime) {
      setFormError("End date/time must be after start date/time.")
      return
    }
    setFormError(null)

    setIsSaving(true)

    try {
      const selectedDepot = availableDepots.find((depot) => depot.name === formData.location)
      // The parent component (SheriffTab) handles the actual API call
      // We just pass the form data up
      await onShiftCreated?.({
        ...formData,
        depotId: selectedDepot?.id ?? null,
      })
      onOpenChange(false)
    } catch (error) {
      // Error is already handled in parent, just keep dialog open
      console.error("Failed to create shift:", error)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[70vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-2xl font-bold">Create Block</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-6 pb-4 space-y-6">
            {formError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {formError}
              </div>
            )}
            {/* Person */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Person</Label>
                <div className="text-sm">{formatPersonShortName(person)}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.startDate && "text-muted-foreground",
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {formData.startDate ? (
                        format(parseISO(formData.startDate), "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                    <CalendarComponent
                      mode="single"
                      selected={formData.startDate ? parseISO(formData.startDate) : undefined}
                      onSelect={(date) =>
                        date &&
                        setFormData((p) => ({
                          ...p,
                          startDate: format(date, "yyyy-MM-dd"),
                          // Keep the end date valid if it now precedes the start date
                          endDate:
                            p.endDate < format(date, "yyyy-MM-dd")
                              ? format(date, "yyyy-MM-dd")
                              : p.endDate,
                        }))
                      }
                      disabled={{ before: parseISO(ukToday()) }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="startTime">Start Time (UK)</Label>
                <div className="relative">
                  <Input
                    id="startTime"
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData((p) => ({ ...p, startTime: e.target.value }))}
                    required
                    className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.endDate && "text-muted-foreground",
                      )}
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {formData.endDate ? (
                        format(parseISO(formData.endDate), "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[100]" align="start" sideOffset={4}>
                    <CalendarComponent
                      mode="single"
                      selected={formData.endDate ? parseISO(formData.endDate) : undefined}
                      onSelect={(date) =>
                        date &&
                        setFormData((p) => ({
                          ...p,
                          endDate: format(date, "yyyy-MM-dd"),
                        }))
                      }
                      disabled={{
                        before: formData.startDate
                          ? parseISO(formData.startDate)
                          : parseISO(ukToday()),
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="endTime">End Time (UK)</Label>
                <div className="relative">
                  <Input
                    id="endTime"
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData((p) => ({ ...p, endTime: e.target.value }))}
                    required
                    className="pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                  <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-black pointer-events-none" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="area">Area</Label>
                <Select
                  value={formData.area}
                  onValueChange={(value) => setFormData((p) => ({ ...p, area: value }))}
                  disabled={personAreas.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        personAreas.length === 0 ? "No sheriff areas on person" : "Select an area"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {personAreas.map((area) => (
                      <SelectItem key={area} value={area}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="type">Type</Label>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Type info"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="start"
                        sideOffset={6}
                        className="max-w-[220px]"
                      >
                        This is the person&apos;s sheriff type from their profile. Update it on the
                        person record if it needs to change.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input id="type" value={displayedType} disabled readOnly />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="role">Role</Label>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Role info"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="start"
                        sideOffset={6}
                        className="max-w-[220px]"
                      >
                        This is the person&apos;s role from their profile. Update it on the person
                        record if it needs to change.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input id="role" value={displayedRole} disabled readOnly />
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData((p) => ({ ...p, location: value }))}
                  disabled={depotsLoading || availableDepots.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        depotsLoading
                          ? "Loading locations..."
                          : availableDepots.length === 0
                            ? "No locations on person"
                            : "Select a location"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDepots.map((depot) => (
                      <SelectItem key={depot.id} value={depot.name}>
                        {depot.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Block Notes</Label>
              <Input
                id="notes"
                placeholder="Add any notes about this block"
                value={formData.notes || ""}
                onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t bg-palette-jungleGreen000">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Block"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default AddShiftDialog
